<template>
  <v-card>
    <v-card-title class="primary white--text py-0" style="line-height: 52px; height: 52px;">
      <span class="title">{{ $t('texts.subscriptionShort') }} #{{ index + 1 }}</span>
      <v-spacer/>
      <v-menu offset-y left>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            icon dark
          >
            <v-icon>settings</v-icon>
          </v-btn>
        </template>
        <v-list class="py-0">
          <v-list-item
            class="py-0"
            @click="showChangeCreditCardDialog(subscriptionGroup.company_id)"
          >
            {{ $t('texts.changeCreditCard') }}
          </v-list-item>
          <v-list-item
            v-if="isCurrentlyCancellable"
            class="py-0"
            @click="showChangeSubscriptionDialog({
              company_id: subscriptionGroup.company_id,
              stripe_plan_id: stripePlanId,
              company_name: subscriptionGroup.name,
              subscription_id: subscription.subscription_id,
              next_possible_cancellation_date: subscription.next_possible_cancellation_date,
            })"
          >
            {{ $t('texts.editSubscription') }}
          </v-list-item>
          <v-list-item
            v-if="isCurrentlyCancellable"
            class="py-0"
            @click="showAddCouponDialog({
              company_id: subscriptionGroup.company_id,
              stripe_plan_id: stripePlanId,
              company_name: subscriptionGroup.name,
              subscription_id: subscription.subscription_id,
              next_possible_cancellation_date: subscription.next_possible_cancellation_date,
            })"
          >
            {{ $t('texts.redeemCoupon') }}
          </v-list-item>
          <v-list-item
            v-if="isCurrentlyCancellable"
            class="py-0"
            @click="showCancelSubscriptionDialog({
              company_id: subscriptionGroup.company_id,
              stripe_plan_id: stripePlanId,
              company_name: subscriptionGroup.name,
              subscription_id: subscription.subscription_id,
              next_possible_cancellation_date: subscription.next_possible_cancellation_date,
              has_active_additional_companies: subscription.has_active_additional_companies,
              type: 'base',
            })"
          >
            {{ $t('texts.cancelSubscription') }}
          </v-list-item>
        </v-list>
      </v-menu>
    </v-card-title>
    <v-card-text>
      <v-container class="pa-0">
        <v-row>
          <v-col :cols="6">{{ $t('texts.company.label') }}</v-col>
          <v-col :cols="6">{{ subscriptionGroup.name }}</v-col>
        </v-row>
        <v-row>
          <v-col :cols="6">
            <template v-if="subscriptionIsPerMonth">
              {{ $t('texts.pricePerMonth') }}
            </template>
            <template v-if="!subscriptionIsPerMonth">
              {{ $t('texts.pricePerYear') }}
            </template>
          </v-col>
          <v-col :cols="6">CHF {{ pricePerMonth(subscription) }}</v-col>
        </v-row>
        <v-row>
          <v-col :cols="6">{{ $t('texts.subscriptionPeriod') }}</v-col>
          <v-col :cols="6">{{ subscription.current_period_starts_at | formatDateShort }} - {{ subscription.current_period_ends_at | formatDateShort }}
            <template v-if="subscription.number_of_test_days">
              <br>
              <i18n path="texts.subscriptionFreeExtension">
                <span>{{ subscription.number_of_test_days }}</span>
              </i18n>
            </template>
          </v-col>
        </v-row>
        <v-row v-if="!subscription.is_cancelled">
          <v-col :cols="6">{{ $t('texts.subscriptionRenewOn') }}</v-col>
          <v-col :cols="6">
            {{ nextRenevalDate | formatDateShort }}
            <v-tooltip
              v-if="subscription.latest_renewal_at"
              top
            >
              <template v-slot:activator="{ on }">
                <v-icon small color="primary" v-on="on">
                  info
                </v-icon>
              </template>
              <i18n path="texts.subscriptionRenewOnInfoText">
                <span>{{ subscription.latest_renewal_at | formatDateShort }}</span>
              </i18n>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row v-else class="red--text">
          <v-col :cols="6">{{ $t('texts.subscriptionTerminatedOn') }}</v-col>
          <v-col :cols="6">{{ cancellationDate | formatDateShort }}</v-col>
        </v-row>
        <v-row>
          <v-col :cols="6">{{ $t('texts.creditCardEndsWith') }}</v-col>
          <v-col :cols="6">{{ subscriptionGroup.last_four_credit_card }}</v-col>
        </v-row>
        <v-row v-if="subscription.discount !== null">
          <v-col :cols="6">{{ $t('texts.couponCode') }}</v-col>
          <v-col :cols="6">
            <template v-if="subscription.discount.coupon.amount_off > 0">
              CHF {{ subscription.discount.coupon.amount_off }}
            </template>
            <template v-else>
              {{ subscription.discount.coupon.percent_off }}%
            </template>
            <template v-if="subscription.discount.end !== '1970-01-01'">
              {{ $t('texts.to') }} {{ subscription.discount.end | formatDateShort }}
            </template>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from 'moment';

export default {
  name: 'SubscriptionItem',
  props: {
    subscriptionGroup: {
      type: Object,
    },
    index: {
      type: Number,
    },
    isCurrentlyCancellable: {
      type: Boolean,
      default: false,
    },
    showChangeCreditCardDialog: {
      type: Function,
      default: () => {},
    },
    showChangeSubscriptionDialog: {
      type: Function,
      default: () => {},
    },
    showAddCouponDialog: {
      type: Function,
      default: () => {},
    },
    showCancelSubscriptionDialog: {
      type: Function,
      default: () => {},
    },
  },
  computed: {
    subscription() {
      return this.subscriptionGroup.subscriptions[0];
    },
    subscriptionIsPerMonth() {
      return this.subscription.items[0].interval === 'month';
    },
    stripePlanId() {
      const subscriptionItems = this.subscription.items;
      const baseSubscription = subscriptionItems.find((item) => item.type === 'base');
      return baseSubscription.stripe_plan_id;
    },
    cancellationDate() {
      return moment(this.subscription.next_possible_cancellation_date).subtract(1, 'days').format('YYYY-MM-DD');
    },
    nextRenevalDate() {
      if (!this.subscription.latest_renewal_at) {
        const diff = moment(this.subscription.next_possible_cancellation_date).diff(this.subscription.latest_renewal_at, 'month', true);
        if (diff <= 1) {
          return moment(this.subscription.next_possible_cancellation_date).add(1, 'years').format('YYYY-MM-DD');
        }
      }
      return this.subscription.next_possible_cancellation_date;
    },
  },
  methods: {
    pricePerMonth(subscription) {
      const pricePerMonth = subscription.items
        .map((item) => parseInt(item.quantity, 10) * parseFloat(item.amount))
        .reduce((total, amount) => total + amount);
      if (subscription.discount === null) {
        return pricePerMonth.toFixed(2);
      }
      return `${this.pricePerMonthDiscount(pricePerMonth, subscription.discount.coupon)} (CHF ${pricePerMonth.toFixed(2)})`;
    },
    pricePerMonthDiscount(pricePerMonth, discount) {
      let priceDiscount = 0;
      if (discount.amount_off > 0) {
        priceDiscount = pricePerMonth - discount.amount_off;
      } else {
        priceDiscount = pricePerMonth - ((pricePerMonth / 100) * discount.percent_off);
      }
      return priceDiscount.toFixed(2);
    },
  },
};
</script>
